import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_SHIFT_OPTIONS } from '../../utils/constants';
import { getClockWindowNameBasedOnSelectedOption } from '../../services/shiftPlanService';
import { formatDateInZone } from '../flowPlanning/dashboard/utils';
import { useAppSelector } from '../../store';
import { selectStationCode } from '../../store/slices/shiftPlanSlice';
import { ReportWindow } from './model';
import { addDays, formatRangeLabel } from './imageQualityUtils';

/** Range presets: trailing N days ending today (inclusive). */
export type RangePreset = '7d' | '14d' | '28d';
const PRESET_DAYS: Record<RangePreset, number> = { '7d': 7, '14d': 14, '28d': 28 };
export const RANGE_PRESETS: { key: RangePreset; label: string; days: number }[] = (
  Object.keys(PRESET_DAYS) as RangePreset[]
).map(key => ({ key, label: key, days: PRESET_DAYS[key] }));
/** Longest window the page offers: the largest preset. Custom From/To dates stay inside it too. */
export const MAX_RANGE_DAYS = 28;
const DEFAULT_PRESET: RangePreset = '7d';

/** Shift filter: both shifts, or one. */
export const ALL_SHIFTS = 'all';
export const SHIFT_OPTIONS = [{ value: ALL_SHIFTS, label: 'All shifts' }, ...DEFAULT_SHIFT_OPTIONS];

/** A window to start from, e.g. carried over from the Associate Scorecards. */
export interface InitialReportWindow {
  preset: RangePreset | null;
  startDate: string;
  endDate: string;
  shift: string;
}

/**
 * Router state for opening the Image Quality page on one associate (from the Associate
 * Scorecards). Router state, not the URL, so the login never lands in the address bar or history.
 */
export interface ImageQualityLinkState {
  associateLogin: string;
  window: InitialReportWindow;
}

/**
 * Date range + shift filter shared by the Image Quality page and the Associate Scorecards:
 * From/To within the last 28 days, 7d/14d/28d presets, and All/Day/Night. The data is daily
 * (operating days, about a day behind), so there is no live view.
 */
export const useReportWindowFilters = (initial?: InitialReportWindow) => {
  const today = useMemo(() => formatDateInZone(Date.now()), []);
  const [preset, setPreset] = useState<RangePreset | null>(
    initial ? initial.preset : DEFAULT_PRESET
  );
  const [startDate, setStartDate] = useState<string>(
    () => initial?.startDate ?? addDays(today, -(PRESET_DAYS[DEFAULT_PRESET] - 1))
  );
  const [endDate, setEndDate] = useState<string>(initial?.endDate ?? today);
  const [shift, setShift] = useState<string>(initial?.shift ?? ALL_SHIFTS);
  // Earliest operating day with data, from the latest response of any view. The calendars don't
  // offer days before it.
  const [dataAvailableFrom, setDataAvailableFrom] = useState<string | undefined>(undefined);
  const noteDataAvailableFrom = useCallback((day?: string) => {
    if (day) setDataAvailableFrom(day);
  }, []);

  const reportWindow: ReportWindow = useMemo(
    () => ({
      startDate,
      endDate,
      shiftType: shift === ALL_SHIFTS ? undefined : getClockWindowNameBasedOnSelectedOption(shift),
    }),
    [startDate, endDate, shift]
  );

  const shiftLabel = SHIFT_OPTIONS.find(option => option.value === shift)?.label ?? shift;
  const windowLabel = `${formatRangeLabel(startDate, endDate)} · ${shift === ALL_SHIFTS ? shiftLabel : `${shiftLabel} shift`}`;

  const applyPreset = (key: RangePreset) => {
    setPreset(key);
    setEndDate(today);
    setStartDate(addDays(today, -(PRESET_DAYS[key] - 1)));
  };

  /** Days the calendars can't pick: after today, older than the last 28 days, or before the data starts. */
  const earliestDay = addDays(today, -(MAX_RANGE_DAYS - 1));
  const isDateDisabled = (date: string) =>
    date > today ||
    date < earliestDay ||
    (dataAvailableFrom !== undefined && date < dataAvailableFrom);

  // Manual dates switch to a custom range. Keep From ≤ To by moving the other end if needed.
  const onFromChange = (date: string) => {
    setPreset(null);
    setStartDate(date);
    if (date > endDate) setEndDate(date);
  };
  const onToChange = (date: string) => {
    setPreset(null);
    setEndDate(date);
    if (date < startDate) setStartDate(date);
  };

  return {
    preset,
    applyPreset,
    /** The window as it stands, to carry over to another page. */
    currentWindow: { preset, startDate, endDate, shift } as InitialReportWindow,
    startDate,
    endDate,
    onFromChange,
    onToChange,
    shift,
    setShift,
    reportWindow,
    windowLabel,
    noteDataAvailableFrom,
    isDateDisabled,
  };
};

export type ReportWindowFilters = ReturnType<typeof useReportWindowFilters>;

/**
 * Which view is showing. Site and Associate views are always the Boson-selected station:
 * other sites appear only as aggregate rows in the All sites ranking (no drill-down). Boson's
 * proxy rejects rnrRCCService requests whose warehouseId isn't the user's station
 * (BosonCommonLib APIGatewayAccessor.validateWarehouseId), and that control is intentional.
 */
export type ImageQualityView =
  | { kind: 'site' }
  | { kind: 'allSites' }
  | { kind: 'associate'; login: string };

/**
 * All state behind the Image Quality page: the report window, plus which view is showing.
 * The site is the station Boson put in the URL (?stationCode=); Boson owns the station.
 * Switching station in Boson returns to the site view.
 */
export const useImageQualityFilters = () => {
  const stationCode = useAppSelector(selectStationCode);
  const linkState = useLocation().state as ImageQualityLinkState | null;
  const windowFilters = useReportWindowFilters(linkState?.window);
  const { reportWindow } = windowFilters;
  const [view, setView] = useState<ImageQualityView>(
    linkState?.associateLogin
      ? { kind: 'associate', login: linkState.associateLogin }
      : { kind: 'site' }
  );

  // An associate picked for one window is meaningless for another; a new Boson station starts
  // over on the site view. Compared with the previous values so the opening view survives mount.
  const previous = useRef({ reportWindow, stationCode });
  useEffect(() => {
    if (previous.current.stationCode !== stationCode) {
      setView({ kind: 'site' });
    } else if (previous.current.reportWindow !== reportWindow) {
      setView(current => (current.kind === 'associate' ? { kind: 'site' } : current));
    }
    previous.current = { reportWindow, stationCode };
  }, [reportWindow, stationCode]);

  return {
    ...windowFilters,
    stationCode,
    view,
    showSite: () => setView({ kind: 'site' }),
    showAllSites: () => setView({ kind: 'allSites' }),
    showAssociate: (login: string) => setView({ kind: 'associate', login }),
  };
};

export type ImageQualityFilters = ReturnType<typeof useImageQualityFilters>;

/** One breadcrumb; the last one is the current view (no onClick). */
export interface IqCrumb {
  key: 'allSites' | 'site' | 'associate';
  label: string;
  onClick?: () => void;
}

/** All sites › <station> › <associate>, following the current view. */
export const breadcrumbsForView = (filters: ImageQualityFilters): IqCrumb[] => {
  const crumbs: IqCrumb[] = [
    { key: 'allSites', label: 'All sites', onClick: filters.showAllSites },
  ];
  if (filters.view.kind !== 'allSites' && filters.stationCode) {
    crumbs.push({ key: 'site', label: filters.stationCode, onClick: filters.showSite });
  }
  if (filters.view.kind === 'associate') {
    crumbs.push({ key: 'associate', label: filters.view.login });
  }
  const last = crumbs[crumbs.length - 1];
  crumbs[crumbs.length - 1] = { key: last.key, label: last.label };
  return crumbs;
};
